import type { SourcePreviewResponse } from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  Button,
  ButtonStyle,
  Combobox,
  type ComboboxListItem,
  FormField,
  SearchableDropdown,
  type SearchableDropdownListItem,
  Spinner,
} from "@prequel/shared-components";

import { type DatasetMethod, METHOD_OPTIONS } from "../types";

import { Divider, FormSection } from "@/components/import/FormLayout";
import PreviewTable from "@/components/import/PreviewTable";

type StepOneProps = {
  canPreview: boolean;
  hasSuccessfulPreview: boolean;
  isLoadingSources: boolean;
  isLoadingTables: boolean;
  isPreviewing: boolean;
  method: DatasetMethod;
  name: string;
  onMethodChange: (method: DatasetMethod) => void;
  onNameChange: (value: string) => void;
  onProviderSelected: (item: SearchableDropdownListItem<string>) => void;
  onRefreshTables: () => void;
  onSourceSelected: (item: SearchableDropdownListItem<string>) => void;
  onSourceTableChange: (value: string) => void;
  onSqlQueryChange: (value: string) => void;
  preview?: SourcePreviewResponse;
  previewSource: () => void;
  providerID: string;
  providerItems: SearchableDropdownListItem<string>[];
  selectedProvider?: SearchableDropdownListItem<string>;
  selectedSource?: SearchableDropdownListItem<string>;
  sourceID: string;
  sourceItems: SearchableDropdownListItem<string>[];
  sourceTable: string;
  sqlQuery: string;
  tableSuggestions: ComboboxListItem[];
};

const StepOne = ({
  canPreview,
  hasSuccessfulPreview,
  isLoadingSources,
  isLoadingTables,
  isPreviewing,
  method,
  name,
  onMethodChange,
  onNameChange,
  onProviderSelected,
  onRefreshTables,
  onSourceSelected,
  onSourceTableChange,
  onSqlQueryChange,
  preview,
  previewSource,
  providerID,
  providerItems,
  selectedProvider,
  selectedSource,
  sourceID,
  sourceItems,
  sourceTable,
  sqlQuery,
  tableSuggestions,
}: StepOneProps) => {
  const previewButtonText = method === "table" ? "Preview Table" : "Preview Query";

  return (
    <>
      <FormSection title="Name the dataset">
        <FormField
          id="dataset-name"
          label="Name"
          type="text"
          value={name}
          onChangeHandler={onNameChange}
          subtext="A descriptive name for this dataset. Defaults to the source table name."
        />
      </FormSection>

      <Divider />

      <FormSection title="Select a Provider">
        <SearchableDropdown
          label="Provider ID"
          items={providerItems}
          selectedItem={selectedProvider}
          setSelectedItem={onProviderSelected}
          emptyText="No providers are available."
        />
      </FormSection>

      <Divider />

      <FormSection title="Select source data">
        {!providerID ? (
          <Alert kind={AlertKind.INFO}>Select a Provider to configure source data.</Alert>
        ) : (
          <div className="space-y-6">
            <SearchableDropdown
              label="Source ID"
              items={sourceItems}
              selectedItem={selectedSource}
              setSelectedItem={onSourceSelected}
              isLoading={isLoadingSources}
              loadingText="Loading sources..."
              emptyText="No sources configured for this provider."
              subtext="Source name (id)"
            />

            {!sourceID ? (
              <Alert kind={AlertKind.INFO}>Select a Source to configure source data.</Alert>
            ) : (
              <>
                <MethodSelector method={method} setMethod={onMethodChange} />
                {method === "table" ? (
                  <SourceTableField
                    sourceTable={sourceTable}
                    setSourceTable={onSourceTableChange}
                    tableSuggestions={tableSuggestions}
                    onRefreshTables={onRefreshTables}
                    isLoadingTables={isLoadingTables}
                  />
                ) : (
                  <SqlQueryField sqlQuery={sqlQuery} setSqlQuery={onSqlQueryChange} />
                )}

                {hasSuccessfulPreview ? (
                  <>
                    <Alert kind={AlertKind.SUCCESS}>Preview successful!</Alert>
                    {preview && <PreviewTable preview={preview} />}
                  </>
                ) : (
                  <Alert kind={AlertKind.WARNING}>
                    Preview data before continuing. Run a sample preview to verify the selection before continuing.
                  </Alert>
                )}

                <Button
                  type={ButtonStyle.PRIMARY}
                  text={
                    isPreviewing ? (
                      <span className="flex items-center">
                        <Spinner.Inline className="mr-3 text-white" />
                        Previewing...
                      </span>
                    ) : (
                      previewButtonText
                    )
                  }
                  onClick={previewSource}
                  disabled={!canPreview}
                />
              </>
            )}
          </div>
        )}
      </FormSection>
    </>
  );
};

const MethodSelector = ({
  method,
  setMethod,
}: {
  method: DatasetMethod;
  setMethod: (method: DatasetMethod) => void;
}) => (
  <fieldset>
    <legend className="mb-2 block text-sm font-medium text-gray-700">Method</legend>
    <div className="flex gap-4">
      {METHOD_OPTIONS.map((option) => (
        <label key={option.key} className="flex items-center gap-2 text-sm text-gray-900">
          <input
            type="radio"
            name="dataset-method"
            value={option.key}
            checked={method === option.key}
            onChange={() => setMethod(option.key)}
            className="
              size-4 border-gray-300 text-emerald-600
              focus:ring-emerald-500
            "
          />
          {option.label}
        </label>
      ))}
    </div>
  </fieldset>
);

const SourceTableField = ({
  sourceTable,
  setSourceTable,
  tableSuggestions,
  onRefreshTables,
  isLoadingTables,
}: {
  sourceTable: string;
  setSourceTable: (value: string) => void;
  tableSuggestions: ComboboxListItem[];
  onRefreshTables: () => void;
  isLoadingTables: boolean;
}) => (
  <Combobox
    label="Source Table"
    value={sourceTable}
    onChange={setSourceTable}
    items={tableSuggestions}
    allowCustomValue
    placeholder="schema.tablename"
    subtext={
      'Fully-qualified source table. Start typing to see matching tables. Use the refresh button to reload visible tables, and use "Preview Table" to verify access. Note that some sources may not support enumerating all available tables based on system capabilities or permissions.'
    }
    isLoading={isLoadingTables}
    onRefresh={onRefreshTables}
    refreshLabel="Refresh visible tables"
    customValueLabel={(value) => value}
  />
);

const SqlQueryField = ({ sqlQuery, setSqlQuery }: { sqlQuery: string; setSqlQuery: (value: string) => void }) => (
  <div>
    <label htmlFor="sql-query" className="block text-sm font-medium text-gray-700">
      SQL Query
    </label>
    <textarea
      id="sql-query"
      name="sql-query"
      required
      rows={8}
      value={sqlQuery}
      onChange={(event) => setSqlQuery(event.target.value)}
      placeholder="select * from schema.tablename"
      className="
        mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm
        placeholder:text-gray-300
        focus:border-primary-500 focus:ring-primary-500
      "
    />
  </div>
);

export default StepOne;
